import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, ChildActivationEnd, ActivatedRoute, ParamMap, Scroll } from '@angular/router';
import { Title } from '@angular/platform-browser';
import {
    Observable,
    BehaviorSubject,
    Subscription,
    Subject,
    mergeMap,
    pairwise,
    startWith,
    merge,
    NEVER,
    of
} from 'rxjs';
import { RouteType } from '../../Shared/enums/route-type.enum';
import { MonitoringRouteModel } from '../../Shared/models/monitoring-route.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MonitoringViewModesEnum } from '../../../Monitor/Shared/enums/monitoring-view-modes.enum';

@Injectable()
export class MonitoringRoutingService implements OnDestroy {

    // Breaks the URL into known pieces so the current location can be evaluated
    // eslint-disable-next-line max-len
    private readonly urlParse: RegExp = /Monitoring\/?(search|mentions|topics|subscriptions|briefings|alerts)?\/?(results|topic|subscription|folder|briefing|canvas|alert|update|saved|dashboard|shared)?\/?([a-zA-Z0-9-]+)?\/?([a-zA-Z0-9-]+)?\/?(update|report)?/;
    /** Matches an agent's section, eg. /Agents/agent/401/mentions or .../analytics */
    private readonly agentFeedUrlParse: RegExp = /Agents\/agent\/([0-9]+)\/(mentions|analytics)/;
    private readonly monitoringRootPath: string = 'Monitoring';

    private queryParamMap: ParamMap;
    private currentRoute: MonitoringRouteModel;
    private currentRouteProvider: BehaviorSubject<MonitoringRouteModel>;
    private cachedNavigationStart: NavigationStart;
    private navOutsideMonitoring$ = new Subject<null>();

    private subscriptions: Subscription[] = [];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private titleService: Title,
        private localizationService: LocalizationService
    ) {
        this.currentRoute = new MonitoringRouteModel(null);
        this.currentRouteProvider = new BehaviorSubject<MonitoringRouteModel>(this.currentRoute);

        this.subscriptions.push(
            this.router.events.subscribe((e) => {
                if (e instanceof NavigationStart) {
                    this.cachedNavigationStart = e;
                }

                if (e instanceof NavigationEnd) {
                    this.updateCurrentRoute(e.urlAfterRedirects);
                    this.setBrowserTitle();
                }
                // child activation end occurs before navigation end
                if (e instanceof ChildActivationEnd) {
                    this.queryParamMap = e.snapshot.queryParamMap;
                }
            }),
        );
        // this.initialRouteCheck(this.router.url);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    /**
     * Checks if the initial route will properly load data, and if not, then go to the default route
     * Default route is first topic if it exists, and otherwise search.
     * @param url The current full URL
     */
    private initialRouteCheck(url: string) {
        const matches = url.match(this.urlParse) || [];

        if (!matches.length) {
            // Not a monitoring route
            return;
        } else {
            switch (matches[1]) {
                case 'search':
                    if (matches[2] === 'update') {
                        this.gotoSearch();
                        break;
                    }
                    if (matches[2] === 'results') {
                        this.gotoSearch();
                        break;
                    }

                    this.updateCurrentRoute(url);
                    break;

                case 'topics':
                case 'subscriptions':
                case 'mentions':
                case 'briefings':
                case 'alerts':
                    this.updateCurrentRoute(url);
                    break;

                default:
                    this.gotoDefault();
                    break;
            }
        }
    }

    /**
     * Updates the current route from a URL
     * @param url The current full URL
     */
    /**
     * Publishes the route for the URL already in the address bar. The service only listens for
     * NavigationEnd, so anything that injects it part way through a navigation - the agent feed,
     * which lives outside the monitoring shell - would otherwise be left with the initial null route
     * and none of the behaviour that hangs off it.
     */
    public syncCurrentRoute(): void {
        this.updateCurrentRoute(this.router.url);
    }

    private updateCurrentRoute(url: string): void {
        const route = this.getRouteModel(url);

        if (!route) {
            this.navOutsideMonitoring$.next(null);
            return;
        }

        this.currentRoute = route;

        this.currentRoute.queryParamMap = this.queryParamMap;
        this.currentRouteProvider.next(this.currentRoute);
    }

    private setBrowserTitle() {
        let child = this.route.firstChild;
        while (child.firstChild) {
            child = child.firstChild;
        }
        if (!!child.snapshot.data['title']) {
            this.titleService.setTitle(
                this.localizationService.transform(
                    child.snapshot.data['title']
                )
            );
        }
    }

    /**
     * Returns a route model based on a url passed in
     *
     * @param url -  url to base route model on
     *
     * @returns MonitoringRouteModel
     */
    getRouteModel(url: string): MonitoringRouteModel {
        // The agent mention feed reuses the monitoring results view, which decides what it offers -
        // the actions menu, the saved coverage date options, the account search - from the route it
        // is on. An agent's feed is saved coverage narrowed to that agent, so it reports as such.
        const agentFeed = url.match(this.agentFeedUrlParse);

        if (agentFeed) {
            const agentRouteType = agentFeed[2] === 'analytics'
                ? RouteType.allSavedCoverageAnalyticsDashboard
                : RouteType.mentions;

            return new MonitoringRouteModel(agentRouteType, agentFeed[1]);
        }

        let matches: string[] = url.match(this.urlParse);
        let route: MonitoringRouteModel;
        if (!matches) {
            return;
        } else {
            matches = matches.filter(x => !!x);
            switch (matches[1]) {
                case 'search':
                    if (matches[2] === 'update') {
                        route = new MonitoringRouteModel(RouteType.updateSearch);
                    } else if (matches[2] === 'results') {
                        route = new MonitoringRouteModel(RouteType.results);
                    } else if (matches[2] === 'dashboard') {
                        route = new MonitoringRouteModel(RouteType.adHocAnalyticsDashboard);
                    }  else if (matches[2] === 'syndicates') {
                        route = new MonitoringRouteModel(RouteType.adHocAnalyticsSyndicates);
                    } else {
                        route = new MonitoringRouteModel(RouteType.search);
                    }
                    break;
                case 'topics':
                    if (matches[2] === 'topic' && matches[3]) {
                        if (matches[4] === 'dashboard') {
                            route = new MonitoringRouteModel(RouteType.topicAnalyticsDashboard, matches[3]);
                        } else if (matches[4] === 'syndicates') {
                            route = new MonitoringRouteModel(RouteType.topicAnalyticsSyndicates, matches[3]);
                        } else {
                            route = new MonitoringRouteModel(RouteType.topic, matches[3]);
                        }
                    } else {
                        route = new MonitoringRouteModel(RouteType.topicsHype);
                    }
                    break;
                case 'subscriptions':
                    if (matches[2] === 'subscription' && matches[3] && matches[4] === 'update') {
                        route = new MonitoringRouteModel(RouteType.subscription, matches[3]);
                    } else if (matches[2] === 'subscription' && matches[3] && matches[4] === 'dashboard') {
                        route = new MonitoringRouteModel(RouteType.subscriptionAnalyticsDashboard, matches[3]);
                    }  else if (matches[2] === 'subscription' && matches[3] && matches[4] === 'syndicates') {
                        route = new MonitoringRouteModel(RouteType.subscriptionAnalyticsSyndicates, matches[3]);
                    } else if (matches[2] === 'subscription' && matches[3]) {
                        route = new MonitoringRouteModel(RouteType.subscriptionResults, matches[3]);
                    } else if (matches[2] === 'subscription') {
                        // New subscription case
                        route = new MonitoringRouteModel(RouteType.subscription);
                    } else if (matches[2] === 'dashboard') {
                        // New subscription case
                        route = new MonitoringRouteModel(RouteType.subscriptionDashboard);
                    } else {
                        route = new MonitoringRouteModel(RouteType.subscriptionsHype);
                    }
                    break;
                case 'mentions':
                    if (matches[2] === 'folder' && matches[3]) {
                        if (matches[4] === 'dashboard') {
                            route = new MonitoringRouteModel(RouteType.folderAnalyticsDashboard, matches[3]);
                        } else if (matches[4] === 'syndicates') {
                            route = new MonitoringRouteModel(RouteType.folderAnalyticsSyndicates, matches[3]);
                        } else {
                            route = new MonitoringRouteModel(RouteType.folder, matches[3]);
                        }
                    } else if (matches[2] === 'saved') {
                        if (matches[3] === 'dashboard') {
                            route = new MonitoringRouteModel(RouteType.allSavedCoverageAnalyticsDashboard);
                        } else if (matches[3] === 'syndicates') {
                            route = new MonitoringRouteModel(RouteType.allSavedCoverageAnalyticsSyndicates);
                        } else {
                            route = new MonitoringRouteModel(RouteType.mentions);
                        }
                    } else {
                        route = new MonitoringRouteModel(RouteType.mentionsHype);
                    }
                    break;
                case 'briefings':
                    if (matches[2] === 'briefing') {
                        const id = matches[3];
                        const sentId = matches[4];
                        if (matches[4] === 'report') {
                            route = new MonitoringRouteModel(RouteType.briefingReport, id);
                        } else if(matches[5] === 'report') {
                            route = new MonitoringRouteModel(RouteType.recurringBriefingReport, id);
                            route.subId = sentId;
                        } else {
                            route = new MonitoringRouteModel(RouteType.briefing, id);
                        }
                    } else if (matches[2] === 'canvas') {
                        const id = matches[3];
                        const sentId = matches[4];
                        if (matches[4] === 'report') {
                            route = new MonitoringRouteModel(RouteType.canvasReport, id);
                        } else if (matches[5] === 'report') {
                            route = new MonitoringRouteModel(RouteType.recurringCanvasReport, id);
                            route.subId = sentId;
                        } else {
                            route = new MonitoringRouteModel(RouteType.canvas, id);
                        }
                    } else {
                        route = new MonitoringRouteModel(RouteType.briefingsHype);
                    }
                    break;
                case 'alerts':
                    if (matches[2] === 'alert') {
                        const id = matches[3];
                        const sentId = matches[4];
                        if (matches[5] === 'report') {
                            route = new MonitoringRouteModel(RouteType.recurringAlertReport, id);
                            route.subId = sentId;
                        } else {
                            route = new MonitoringRouteModel(RouteType.alert, id);
                        }
                    } else {
                        route = new MonitoringRouteModel(RouteType.alertsHype);
                    }
                    break;
                default:
                    // Inside monitoring, but unrecognized, so go to default
                    route = new MonitoringRouteModel(RouteType.default);
                    break;
            }
        }

        return route;
    }

    /**
     * Given an observable that tracks the current mode, returns an observable that tracks the
     * current mode and whether it has changed. Useful because subscriptions to this service's currentRoute
     * fire even when we are outside the feature. This allows us to determine whether we are leaving or entering
     * the feature (and subfeature/mode).
     * @param getCurrentMode$ An observable that emits the current mode on route event
     * @returns An observable that emits on route event, detailing whether the mode has changed from previous value
     */
    public getModeChange(
        getCurrentMode$: Observable<number>
    ): Observable<{ isModeChange: boolean; currentMode: number}> {
        return merge(
            this.navOutsideMonitoring$,
            getCurrentMode$
        ).pipe(
            startWith(null),
            pairwise(),
            mergeMap(([prev, curr]) => {
                if (curr === null) {
                    // We are currently outside monitoring
                    return NEVER;
                } else {
                    // False means navigation with a subfeature
                    // True means navigation to a new subfeature
                    return of({
                        isModeChange: prev !== curr,
                        currentMode: curr
                    });
                }
            }),
        );
    }

    /**
     * Gets an observable for the current route
     *
     * @returns An observable that's updated each time the route changes
     */
    public getCurrentRoute(): Observable<MonitoringRouteModel> {
        return this.currentRouteProvider;
    }

    /**
     * Routes to a monitoring section
    */
    private goto(path: (string | number)[], replaceURL: boolean = false, queryParams: any = {}): void {
        this.router.navigate(
            [
                this.monitoringRootPath,
                ...path
            ],
            // Optionally replace the previous URL in the browser history
            {
                replaceUrl: replaceURL,
                queryParams: queryParams
            }
        );
    }

    /**
     * Updates the route to send the user to the search page
     */
    public gotoDefault(): void {
        this.gotoTopics();
    }

    /**
     * Updates the route to send the user to the search page
     */
    public gotoSearch(copyTopicId?: number): void {
        if (copyTopicId) {
            this.goto(['search'], undefined, { copy: copyTopicId });
        } else {
            this.goto(['search']);
        }
    }

    /**
     * Updates the route to send the user to the update search page
    */
    public gotoUpdateSearch(): void {
        this.goto(['search', 'update']);
    }

    /**
     * Updates the route to send the user to the results page
     */
    public gotoResults(options?: any): void {
        const queryParams = options?.queryParams || {};
        const replaceURL = options?.replaceUrl || false;
        this.goto(['search', 'results'], replaceURL, queryParams);
    }

    /**
     * Updates the route to send the user to the saved mentions page
     */
    public gotoSavedCoverageHype(options?: any): void {
        const queryParams = options?.queryParams || {};
        const replaceURL = options?.replaceUrl || false;
        this.goto(['mentions'],replaceURL, queryParams);
    }

    /**
     * Updates the route to send the user to the saved mentions page
     */
    public gotoSavedCoverage(): void {
        this.goto(['mentions', 'saved']);
    }

    /**
     * Updates the route to send the user to a folder view of saved mentions
     * @param id A folder id
     */
    public gotoFolder(id: string | number): void {
        this.goto(['mentions', 'folder', '' + id]);
    }

    /**
     * Updates the route to send the user to the search page
     */
    public gotoTopics(options?: any): void {
        const queryParams = options?.queryParams || {};
        const replaceURL = options?.replaceUrl || false;
        this.goto(['topics'], replaceURL, queryParams);
    }

    /**
     * Updates the route to send the user to a topic view of mentions
     * @param id A topic id
     */
    public gotoTopic(id: string | number): void {
        this.goto(['topics', 'topic', id]);
    }

    /**
     * Updates the route to send the user to topic monitoring analytics dashboard
     */
    public gotoTopicAnalyticsDashboard(id: string | number): void {
        this.goto(['topics', 'topic', id, 'dashboard']);
    }

    /**
     * Updates the route to send the user to folder monitoring analytics dashboard
     */
    public gotoFolderAnalyticsDashboard(id: string | number): void {
        this.goto(['mentions', 'folder', id, 'dashboard']);
    }

    /**
     * Updates the route to send the user to folder monitoring analytics dashboard
     */
    public gotoSubscriptionAnalyticsDashboard(id: string | number): void {
        this.goto(['subscriptions', 'subscription', id, 'dashboard']);
    }

    /**
     * Updates the route to send the user to folder monitoring analytics dashboard
     */
    public gotoAllSavedCoverageAnalyticsDashboard(): void {
        this.goto(['mentions', 'saved', 'dashboard']);
    }

    /**
     * Updates the route to send the user to folder monitoring analytics dashboard
     */
    public gotoAdHocAnalyticsDashboard(): void {
        this.goto(['search', 'dashboard']);
    }

    /**
     * Updates the route to send the user to topic monitoring analytics dashboard
     */
    public gotoTopicAnalyticsSyndicates(id: string | number): void {
        this.goto(['topics', 'topic', id, 'syndicates']);
    }

    /**
     * Updates the route to send the user to folder monitoring analytics syndicates
     */
    public gotoFolderAnalyticsSyndicates(id: string | number): void {
        this.goto(['mentions', 'folder', id, 'syndicates']);
    }

    /**
     * Updates the route to send the user to folder monitoring analytics syndicates
     */
    public gotoSubscriptionAnalyticsSyndicates(id: string | number): void {
        this.goto(['subscriptions', 'subscription', id, 'syndicates']);
    }

    /**
     * Updates the route to send the user to folder monitoring analytics syndicates
     */
    public gotoAllSavedCoverageAnalyticsSyndicates(): void {
        this.goto(['mentions', 'saved', 'syndicates']);
    }

    /**
     * Updates the route to send the user to folder monitoring analytics syndicates
     */
    public gotoAdHocAnalyticsSyndicates(): void {
        this.goto(['search', 'syndicates']);
    }

    /**
     * Updates the route to send the user to the subscriptions feature
    */
    public gotoSubscriptionHype(options?: any): void {
        const queryParams = options?.queryParams || {};
        const replaceURL = options?.replaceUrl || false;
        this.goto(['subscriptions'], replaceURL, queryParams);
    }

    /**
     * Updates the route to send the user to the subscriptions feature
    */
    public gotoSubscriptionDashboard(): void {
        this.goto(['subscriptions', 'dashboard']);
    }

    /**
     * Updates the route to send the user to the update media subscription view
    */
    public gotoNewMediaSubscription(): void {
        this.goto(['subscriptions', 'subscription']);
    }

    /**
     * Updates the route to send the user to the update media subscription view
     */
    public gotoMediaSubscriptionResults(id: string | number): void {
        this.goto(['subscriptions', 'subscription', '' + id]);
    }

    /**
     * Updates the route to send the user to the update media subscription view
    */
    public gotoUpdateMediaSubscription(id: string | number): void {
        this.goto(['subscriptions', 'subscription', '' + id, 'update']);
    }

    /**
     * Updates the route to send the user to the briefings hype page
     */

    public gotoBriefings(options?: any): void {
        const queryParams = options?.queryParams || {};
        const replaceURL = options?.replaceUrl || false;

        this.goto(['briefings'], replaceURL, queryParams);
    }

    /**
     * Updates the route to send the user to the new briefing page
     */
    public gotoNewBriefing(): void {
        this.goto(['briefings', 'newBriefing']);
    }

    /**
     * Updates the route to send the user to the new canvas page
     * @param agentPrompt Optional natural language prompt to auto-submit via the Canvas Creation Agent
     */
    public gotoNewCanvas(agentPrompt?: string): void {
        this.router.navigate(
            [this.monitoringRootPath, 'briefings', 'newCanvas'],
            agentPrompt ? { state: { agentPrompt } } : {}
        );
    }

    /**
     * Updates the route to send the user to a canvas compose page
     */
    public gotoCanvas(id?: string | number, quickBriefing: boolean = false): void {
        this.gotoBriefingComposePath('canvas', id, quickBriefing);
    }

    /**
     * Updates the route to send the user to an individual briefing
     */
    public gotoBriefing(id?: string | number, quickBriefing: boolean = false): void {
        this.gotoBriefingComposePath('briefing', id, quickBriefing);
    }

    private gotoBriefingComposePath(subPath: string, id?: string | number, quickBriefing: boolean = false): void {
        const path: (string | number)[] = ['briefings', subPath];
        if (id) {
            path.push('' + id);
        }
        this.goto(path, undefined, quickBriefing ? { quickBriefing: true } : {});
    }

    /**
     * Updates the route to send the user to an individual briefing report
     */
    public gotoBriefingReport(id?: string | number): void {
        const path = ['briefings', 'briefing', id, 'report'];

        this.goto(path);
    }

    /**
     * Updates the route to send the user to an individual canvas report
     */
    public gotoCanvasReport(id?: string | number): void {
        const path = ['briefings', 'canvas', id, 'report'];

        this.goto(path);
    }

    /**
     * Updates the route to send the user to an individual recurring canvas report
     */
    public gotoRecurringCanvasReport(id: string | number, sentId: string | number): void {
        const path = ['briefings', 'canvas', id, sentId, 'report'];

        this.goto(path);
    }

    /**
     * Updates the route to send the user to an individual recurring briefing report
     */
    public gotoRecurringBriefingReport(id: string | number, sentId: string | number): void {
        const path = ['briefings', 'briefing', id, sentId, 'report'];

        this.goto(path);
    }

    /**
     * Updates the route to send the user to the alerts hype page
     */
    public gotoAlerts(options?: any): void {
        const queryParams = options?.queryParams || {};
        const replaceURL = options?.replaceUrl || false;

        this.goto(['alerts'], replaceURL, queryParams);

    }

    /**
     * Updates the route to send the user to an individual alert
     */
    public gotoAlert(id?: string | number): void {
        const path = ['alerts', 'alert'];
        if (id) {
            path.push('' + id);
        }
        this.goto(path);
    }

    /**
     * Updates the route to send the user to an individual recurring briefing report
     */
    public gotoRecurringAlertReport(id: string | number, sentId: string | number): void {
        const path = ['alerts', 'alert', id, sentId, 'report'];

        this.goto(path);
    }


    /**
     * Returns the last cached NavigationStart object
     *
     * @returns NavigationStart
     */
    public getNextRoute(): MonitoringRouteModel {
        return this.getRouteModel(this.cachedNavigationStart.url);
    }

    handleViewModeRouteChange(mode: MonitoringViewModesEnum, currentRoute: MonitoringRouteModel) {
        switch(currentRoute.type) {
            case RouteType.folderAnalyticsDashboard:
            case RouteType.folderAnalyticsSyndicates:
            case RouteType.folder:
                if (mode === MonitoringViewModesEnum.SYNDICATE) {
                    this.gotoFolderAnalyticsSyndicates(currentRoute.value);
                } else if (mode === MonitoringViewModesEnum.DASHBOARD) {
                    this.gotoFolderAnalyticsDashboard(currentRoute.value);
                } else {
                    this.gotoFolder(currentRoute.value);
                }
                break;
            case RouteType.allSavedCoverageAnalyticsDashboard:
            case RouteType.allSavedCoverageAnalyticsSyndicates:
            case RouteType.mentions:
                if (mode === MonitoringViewModesEnum.SYNDICATE) {
                    this.gotoAllSavedCoverageAnalyticsSyndicates();
                }  else if (mode === MonitoringViewModesEnum.DASHBOARD) {
                    this.gotoAllSavedCoverageAnalyticsDashboard();
                } else {
                    this.gotoSavedCoverage();
                }
                break;
            case RouteType.topicAnalyticsDashboard:
            case RouteType.topicAnalyticsSyndicates:
            case RouteType.topic:
                if (mode === MonitoringViewModesEnum.SYNDICATE) {
                    this.gotoTopicAnalyticsSyndicates(currentRoute.value);
                } else if (mode === MonitoringViewModesEnum.DASHBOARD) {
                    this.gotoTopicAnalyticsDashboard(currentRoute.value);
                } else {
                    this.gotoTopic(currentRoute.value);
                }
                break;
            case RouteType.adHocAnalyticsDashboard:
            case RouteType.adHocAnalyticsSyndicates:
            case RouteType.results:
                if (mode === MonitoringViewModesEnum.SYNDICATE) {
                    this.gotoAdHocAnalyticsSyndicates();
                } else if (mode === MonitoringViewModesEnum.DASHBOARD) {
                    this.gotoAdHocAnalyticsDashboard();
                }  else {
                    this.gotoResults(currentRoute.value);
                }
                break;
            case RouteType.subscriptionAnalyticsDashboard:
            case RouteType.subscriptionAnalyticsSyndicates:
            case RouteType.subscriptionResults:
                if (mode === MonitoringViewModesEnum.SYNDICATE) {
                    this.gotoSubscriptionAnalyticsSyndicates(currentRoute.value);
                } else if (mode === MonitoringViewModesEnum.DASHBOARD) {
                    this.gotoSubscriptionAnalyticsDashboard(currentRoute.value);
                }  else {
                    this.gotoMediaSubscriptionResults(currentRoute.value);
                }
                break;
        }
    }

}

